/**
 * Stream B — Campaign Variants (redirects to A/B Testing page)
 * Legacy route kept for backward compatibility.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { ChevronLeft, ChevronRight, Target, BarChart3 } from "lucide-react";
import { SkeletonCard } from "@/components/skeleton";

interface EmailVariant {
  id: string;
  variantLabel: string;
  subjectOverride: string | null;
  percentage: number;
  sentCount: number;
  openCount: number;
  clickCount: number;
  conversionCount: number;
  isWinner: boolean;
}

function TargetingBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-surface)] px-2.5 py-0.5 text-xs font-medium text-[var(--color-muted-foreground)]">
      <Target className="h-3 w-3" />
      {label}
    </span>
  );
}

export default function CampaignVariantsPage() {
  const { id } = useParams<{ id: string }>();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [variants, setVariants] = useState<EmailVariant[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchVariants = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiFetch<{ data: { variants: EmailVariant[] } }>(`/email-marketing/campaigns/${id}/ab-test`);
      setVariants(res.data.variants);
    } catch {
      setVariants([]);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchVariants(); }, [fetchVariants]);

  const current = variants[currentIdx];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Content Variants</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">Campaign {id?.slice(0, 8)} — variant comparison.</p>
      </div>

      {isLoading ? (
        <SkeletonCard className="h-64 rounded-xl" />
      ) : variants.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-16 text-center">
          <p className="text-[var(--color-muted-foreground)]">No variants configured.</p>
          <a href={`/email/campaigns/${id}/ab-test`} className="mt-2 inline-block text-sm text-[var(--color-primary)] hover:underline">
            Set up A/B test
          </a>
        </div>
      ) : (
        <>
          {/* Carousel */}
          <div className="relative">
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
              {current && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-[var(--color-foreground)]">{current.variantLabel}</h2>
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {currentIdx + 1} / {variants.length}
                    </span>
                  </div>

                  <div className="space-y-3 mb-4">
                    {current.subjectOverride && (
                      <div>
                        <div className="text-xs text-[var(--color-muted-foreground)] uppercase">Subject</div>
                        <div className="text-sm font-medium text-[var(--color-foreground)]">{current.subjectOverride}</div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-wrap mb-4">
                    <TargetingBadge label={`${current.percentage}% split`} />
                    {current.isWinner && <TargetingBadge label="Winner" />}
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="stagger-item rounded-lg bg-[var(--color-surface)] p-3">
                      <div className="text-xs text-[var(--color-muted-foreground)]">Sent</div>
                      <div className="text-lg font-bold text-[var(--color-foreground)]">{current.sentCount}</div>
                    </div>
                    <div className="stagger-item rounded-lg bg-[var(--color-surface)] p-3">
                      <div className="text-xs text-[var(--color-muted-foreground)]">Open Rate</div>
                      <div className="text-lg font-bold text-[var(--color-foreground)]">
                        {current.sentCount > 0 ? ((current.openCount / current.sentCount) * 100).toFixed(1) : "0"}%
                      </div>
                    </div>
                    <div className="stagger-item rounded-lg bg-[var(--color-surface)] p-3">
                      <div className="text-xs text-[var(--color-muted-foreground)]">Click Rate</div>
                      <div className="text-lg font-bold text-[var(--color-foreground)]">
                        {current.openCount > 0 ? ((current.clickCount / current.openCount) * 100).toFixed(1) : "0"}%
                      </div>
                    </div>
                    <div className="stagger-item rounded-lg bg-[var(--color-surface)] p-3">
                      <div className="text-xs text-[var(--color-muted-foreground)]">Conversions</div>
                      <div className="text-lg font-bold text-[var(--color-success)]">{current.conversionCount}</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {variants.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setCurrentIdx((prev) => Math.max(0, prev - 1))}
                  disabled={currentIdx === 0}
                  className="rounded-lg border border-[var(--color-border)] p-2 hover:bg-[var(--color-surface)] disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentIdx((prev) => Math.min(variants.length - 1, prev + 1))}
                  disabled={currentIdx === variants.length - 1}
                  className="rounded-lg border border-[var(--color-border)] p-2 hover:bg-[var(--color-surface)] disabled:opacity-30"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* A/B Comparison table */}
          {variants.length > 1 && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="text-sm font-semibold text-[var(--color-foreground)] mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> A/B Comparison
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="py-2 text-left text-[var(--color-muted-foreground)]">Variant</th>
                    <th className="py-2 text-right text-[var(--color-muted-foreground)]">Split</th>
                    <th className="py-2 text-right text-[var(--color-muted-foreground)]">Opens</th>
                    <th className="py-2 text-right text-[var(--color-muted-foreground)]">Clicks</th>
                    <th className="py-2 text-right text-[var(--color-muted-foreground)]">Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => (
                    <tr key={v.id} className="stagger-item border-b border-[var(--color-border)]">
                      <td className="py-2 text-[var(--color-foreground)] font-medium">
                        {v.variantLabel}
                        {v.isWinner && <span className="ml-1 text-xs text-[var(--color-success)]">Winner</span>}
                      </td>
                      <td className="py-2 text-right text-[var(--color-foreground)]">{v.percentage}%</td>
                      <td className="py-2 text-right text-[var(--color-foreground)]">
                        {v.sentCount > 0 ? ((v.openCount / v.sentCount) * 100).toFixed(1) : "0"}%
                      </td>
                      <td className="py-2 text-right text-[var(--color-foreground)]">
                        {v.openCount > 0 ? ((v.clickCount / v.openCount) * 100).toFixed(1) : "0"}%
                      </td>
                      <td className="py-2 text-right text-[var(--color-success)] font-medium">{v.conversionCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
